import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';

import { queryStrings } from '@trqk/resolvers';
import {
    updateAndReplaceURLSearchParams,
    getStatementsURLSearchParam,
    removeNulls,
} from '@trqk/utils';

import { useDetectionTracks, useEntities, useToast } from '../../../hooks';
import {
    WorkDetailChild,
    WorkDetailLoadingError,
    WorkDetailStatementSelectionProvider,
    WorkDetailStatementSelectionContext,
} from './components';
import { URL_PARAMS } from '../../../constants';

const { GET_TRQK_SONG } = queryStrings;

const GET_TRQK_AUDIO = gql`
    query getTrqkAudio($id: ID!) {
        getTrqkAudio(id: $id) {
            id
            asset_url
            deleted
            detection_history {
                timestamp
                status
                source
            }
            detection_status
            filename
            song_id
            user_id
        }
    }
`;

/**
 * Fetches data and manages URL query parameter state for a detailed view of a work, as controlled
 * by the current entity and statement. A work consists of either a `trqkSong`, a `trqkAudio`, or
 * both. The `trqkSong` is a work from a PRO statement that was ingested. A `trqkAudio` represents
 * an audio file that a user uploaded.
 *
 * A work can exist in three states:
 * 1. A `trqkAudio` with a `song_id` represents a statement work that was matched with an audio
 * file
 * 2. A `trqkAudio` without a `song_id` is audio that has not been matched to a statement, i.e. an
 * "unmatched work".
 * 3. A `trqkSong` without an associated `audio_id` is a statement work that was not matched with
 * an audio file
 *
 * We have to be able to react to changes in the status of a work within these three states.
 * Initially, we pull the `audio_id` and `song_id` from URL parameters, and use these ids to fetch
 * the `trqkAudio` and `trqkSong`. However, a user may match or un-match a work, which requires us
 * to update the state of these objects internally (in addition from the state implied by the query
 * params).
 */
export const WorkDetail = (): JSX.Element => {
    const history = useHistory();
    const [historySteps, setHistorySteps] = useState<number>(0);

    history.listen(() => {
        setHistorySteps((prev: number): number => prev + 1);
    });

    const { add: addToast } = useToast();
    const searchParams = new URLSearchParams(history.location.search);
    const trqkAudioIdQueryParam = searchParams.get(URL_PARAMS.AUDIO_ID);
    const trqkSongIdQueryParam = searchParams.get(URL_PARAMS.SONG_ID);

    useEffect(
        () =>
            /**
             * When component unmounts, delete the `audio_id` and `song_id` URL params
             */
            () => {
                updateAndReplaceURLSearchParams(history, [
                    {
                        key: URL_PARAMS.AUDIO_ID,
                        value: null,
                    },
                    {
                        key: URL_PARAMS.SONG_ID,
                        value: null,
                    },
                    {
                        key: URL_PARAMS.DETAIL_STATEMENT_IDS,
                        value: null,
                    },
                ]);
            },
        [history],
    );

    const [trqkSongId, setTrqkSongId] =
        useState<Maybe<string>>(trqkSongIdQueryParam);

    const [trqkAudioId, setTrqkAudioId] = useState<Maybe<string>>(
        trqkAudioIdQueryParam,
    );

    const [trqkSong, setTrqkSong] = useState<TrqkSong | null>(null);
    const [trqkAudio, setTrqkAudio] = useState<TrqkAudio | null>(null);

    // If there is a problem loading data for this work, redirect to previous page
    const goToPreviousPage = useCallback(() => {
        addToast({
            RenderContent: WorkDetailLoadingError,
            kind: 'warning-card',
        });

        setTimeout((): void => {
            history.go(-historySteps - 1);
        }, 5000);
    }, [historySteps, addToast]);

    const [selectedStatementIds, setSelectedStatementIds] = useState<string[]>(
        [],
    );

    const [trqkSongStatementIds, setTrqkSongStatementIds] = useState<string[]>(
        [],
    );

    const [getTrqkSong, getTrqkSongQuery] = useLazyQuery<{
        getTrqkSong: TrqkSong;
    }>(GET_TRQK_SONG, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data): void => {
            const trqkSongData = data?.getTrqkSong ?? null;

            if (trqkSongData === null) {
                goToPreviousPage();
            } else {
                const audio_id = trqkSongData.audio_id ?? null;

                setTrqkSong(trqkSongData);

                // Keep URL params and trqkAudio state in sync with the data returned by getTrqkSong
                if (audio_id !== trqkAudioId) {
                    setTrqkAudioId(audio_id);
                    updateAndReplaceURLSearchParams(history, [
                        {
                            key: URL_PARAMS.AUDIO_ID,
                            value: audio_id,
                        },
                    ]);

                    if (audio_id === null) {
                        setTrqkAudio(null);
                    }
                }

                if (trqkSongData.statements) {
                    setTrqkSongStatementIds(
                        removeNulls<string>(trqkSongData.statements),
                    );
                }
            }
        },
        onError: () => {
            goToPreviousPage();
        },
    });

    useEffect(() => {
        if (trqkSongId) {
            getTrqkSong({
                variables: {
                    id: trqkSongId,
                },
            });
        }
    }, [trqkSongId]);

    const [getTrqkAudio, getTrqkAudioQuery] = useLazyQuery<{
        getTrqkAudio: TrqkAudio;
    }>(GET_TRQK_AUDIO, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data): void => {
            const trqkAudioData = data?.getTrqkAudio ?? null;

            if (trqkAudioData === null) {
                goToPreviousPage();
            } else {
                const { deleted, song_id } = trqkAudioData;

                if (deleted) {
                    // If this audio file was deleted:
                    if (trqkSongId === null && song_id === null) {
                        // and there is no song_id, go to previous page
                        goToPreviousPage();
                    } else {
                        // otherwise, set trqkAudio to null and remove audio_id from URL params
                        setTrqkAudio(null);

                        updateAndReplaceURLSearchParams(history, [
                            {
                                key: URL_PARAMS.AUDIO_ID,
                                value: null,
                            },
                        ]);
                    }
                } else {
                    updateAndReplaceURLSearchParams(history, [
                        {
                            key: URL_PARAMS.SONG_ID,
                            value: song_id ?? null,
                        },
                    ]);

                    setTrqkAudio(trqkAudioData);
                }

                // Keep URL params and trqkSong state in sync with the data returned by getTrqkAudio
                if (song_id !== trqkSongId) {
                    setTrqkSongId(song_id ?? null);

                    updateAndReplaceURLSearchParams(history, [
                        {
                            key: URL_PARAMS.SONG_ID,
                            value: song_id ?? null,
                        },
                    ]);

                    if (song_id === null) {
                        setTrqkSong(null);
                    }
                }
            }
        },
        onError: () => {
            goToPreviousPage();
        },
    });

    useEffect(() => {
        if (trqkAudioId) {
            getTrqkAudio({
                variables: {
                    id: trqkAudioId,
                },
            });
        }
    }, [trqkAudioId]);

    const { setDetectionTracks } = useDetectionTracks();

    const onCreateTrqkAudio = useCallback(
        (newTrqkAudio: TrqkAudio | null): void => {
            const newTrqkAudioId = newTrqkAudio?.id ?? null;

            // Keep URL params and trqkAudio state in sync with the data returned by createTrqkAudio
            if (newTrqkAudioId !== trqkAudioId) {
                setTrqkAudioId(newTrqkAudioId);
                updateAndReplaceURLSearchParams(history, [
                    {
                        key: URL_PARAMS.AUDIO_ID,
                        value: newTrqkAudioId,
                    },
                ]);
            }

            setTrqkAudio(newTrqkAudio);
        },
        [history, trqkAudioId],
    );

    const onUnmatchWork = useCallback((): void => {
        if (trqkAudioId !== null) {
            // If we have unmatched a trqkAudio, remove the trqkSong details from the matching
            // detectionTrack in the global detectionTracks list
            setDetectionTracks((prev: DetectionTrack[]): DetectionTrack[] =>
                prev.map((detectionTrack: DetectionTrack): DetectionTrack => {
                    if (detectionTrack.audio_id === trqkAudioId) {
                        return {
                            ...detectionTrack,
                            song_id: null,
                            percent_shares: null,
                            title: null,
                            work_ids: null,
                        };
                    }

                    return detectionTrack;
                }),
            );
        }

        setTrqkAudioId(null);
        setTrqkAudio(null);
        updateAndReplaceURLSearchParams(history, [
            {
                key: URL_PARAMS.AUDIO_ID,
                value: null,
            },
        ]);
    }, [history, trqkAudioId, setDetectionTracks]);

    const { sendEntitySelectionEvent } = useEntities();

    useEffect(() => {
        // Disable entity selection for WorkDetail page
        sendEntitySelectionEvent({
            type: 'SET_DISABLE_SELECTION',
            value: {
                disableSelection: true,
            },
        });

        return (): void => {
            // Enable entity selection when leaving WorkDetail page
            sendEntitySelectionEvent({
                type: 'SET_DISABLE_SELECTION',
                value: {
                    disableSelection: false,
                },
            });
        };
    }, [sendEntitySelectionEvent]);

    // If there is no trqkSongId, ensure that entity selection is set to "View All" for this page
    // and disable entity selection
    useEffect(() => {
        if (trqkSongId === null) {
            sendEntitySelectionEvent({
                type: 'SET_DISABLE_SELECTION',
                value: {
                    disableSelection: true,
                },
            });

            sendEntitySelectionEvent({
                type: 'SET_VIEW_ALL',
                value: {
                    viewAll: true,
                },
            });
        }

        return (): void => {
            sendEntitySelectionEvent({
                type: 'SET_DISABLE_SELECTION',
                value: {
                    disableSelection: false,
                },
            });

            sendEntitySelectionEvent({
                type: 'SET_VIEW_ALL',
                value: {
                    viewAll: false,
                },
            });
        };
    }, [trqkSongId, sendEntitySelectionEvent]);

    const onSelectStatements = useCallback(
        (nextSelectedIds: string[]): void => {
            if (nextSelectedIds.length > 0) {
                setSelectedStatementIds(nextSelectedIds);

                const statementsSearchParam =
                    getStatementsURLSearchParam(nextSelectedIds);

                // When statements are selected:
                updateAndReplaceURLSearchParams(history, [
                    // Update `detail_statement_ids` URL param value
                    {
                        key: URL_PARAMS.DETAIL_STATEMENT_IDS,
                        value: statementsSearchParam,
                    },
                    // Remove `detail_statement_preset_primary` and `detail_statement_preset_fallback` from URL
                    // params
                    {
                        key: URL_PARAMS.DETAIL_STATEMENT_PRESET_PRIMARY,
                        value: null,
                    },
                    {
                        key: URL_PARAMS.DETAIL_STATEMENT_PRESET_FALLBACK,
                        value: null,
                    },
                ]);
            }
        },
        [history],
    );

    if (trqkSongId && trqkSongStatementIds === null) {
        return <></>;
    }

    return (
        <WorkDetailStatementSelectionProvider
            trqkSongStatementIds={trqkSongStatementIds}
            onSelectStatements={onSelectStatements}
            StatementSelectionContext={WorkDetailStatementSelectionContext}
        >
            <WorkDetailChild
                loadingTrqkAudio={
                    trqkAudioId !== null && (getTrqkAudioQuery?.loading ?? true)
                }
                loadingTrqkSong={
                    trqkSongId !== null && (getTrqkSongQuery?.loading ?? true)
                }
                onCreateTrqkAudio={onCreateTrqkAudio}
                onUnmatchWork={onUnmatchWork}
                selectedStatementIds={selectedStatementIds}
                trqkAudio={trqkAudio}
                trqkSong={trqkSong}
            />
        </WorkDetailStatementSelectionProvider>
    );
};
